import { useCallback, useEffect, useRef, useState } from "react";

type BrowserType = "chrome" | "firefox" | "safari" | "edge" | "unknown";

interface VoicePreference {
  patterns: string[];
  fallbackLangs: string[];
}

type VoiceConfig = {
  [browser in BrowserType]: {
    [lang: string]: VoicePreference;
  };
};

export interface UseTextToSpeechOptions {
  lang?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: SpeechSynthesisErrorEvent) => void;
}

export interface UseTextToSpeechReturn {
  speak: (text: string) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  isSpeaking: boolean;
  isPaused: boolean;
  isSupported: boolean;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  setVoice: (voice: SpeechSynthesisVoice) => void;
  messageId: string | null;
  setMessageId: (messageId: string) => void;
}

const VOICE_CONFIG: VoiceConfig = {
  chrome: {
    fr_FR: {
      patterns: ["Google français", "Microsoft Hortense", "Marie", "Thomas"],
      fallbackLangs: ["fr-CA", "fr-BE", "fr-CH"],
    },
    en_US: {
      patterns: ["Google US English", "Microsoft Zira", "Alex", "Samantha"],
      fallbackLangs: ["en-GB", "en-AU", "en-CA"],
    },
    en_GB: {
      patterns: ["Google UK English Female", "Microsoft Hazel", "Daniel", "Kate"],
      fallbackLangs: ["en-US", "en-AU", "en-CA"],
    },
    es_ES: {
      patterns: ["Google español", "Microsoft Helena", "Mónica", "Jorge"],
      fallbackLangs: ["es-MX", "es-AR", "es-CO"],
    },
    de_DE: {
      patterns: ["Google Deutsch", "Microsoft Hedda", "Anna", "Markus"],
      fallbackLangs: ["de-AT", "de-CH"],
    },
    it_IT: {
      patterns: ["Google italiano", "Microsoft Elsa", "Alice", "Luca"],
      fallbackLangs: ["it-CH"],
    },
  },
  firefox: {
    fr_FR: {
      patterns: ["Thomas", "French", "Marie"],
      fallbackLangs: ["fr-CA", "fr-BE", "fr-CH"],
    },
    en_US: {
      patterns: ["English", "US English", "Alex", "Samantha"],
      fallbackLangs: ["en-GB", "en-AU", "en-CA"],
    },
    en_GB: {
      patterns: ["UK English", "British", "Daniel", "Kate"],
      fallbackLangs: ["en-US", "en-AU", "en-CA"],
    },
    es_ES: {
      patterns: ["español", "Spanish", "Mónica", "Jorge"],
      fallbackLangs: ["es-MX", "es-AR", "es-CO"],
    },
    de_DE: {
      patterns: ["Deutsch", "German", "Anna", "Markus"],
      fallbackLangs: ["de-AT", "de-CH"],
    },
    it_IT: {
      patterns: ["italiano", "Italian", "Alice", "Luca"],
      fallbackLangs: ["it-CH"],
    },
  },
  safari: {
    fr_FR: {
      patterns: ["Marie", "Thomas", "Amélie", "français"],
      fallbackLangs: ["fr-CA", "fr-BE", "fr-CH"],
    },
    en_US: {
      patterns: ["Alex", "Samantha", "Victoria", "Fred"],
      fallbackLangs: ["en-GB", "en-AU", "en-CA"],
    },
    en_GB: {
      patterns: ["Daniel", "Kate", "Serena", "Oliver"],
      fallbackLangs: ["en-US", "en-AU", "en-CA"],
    },
    es_ES: {
      patterns: ["Mónica", "Jorge", "Marisol", "Carlos"],
      fallbackLangs: ["es-MX", "es-AR", "es-CO"],
    },
    de_DE: {
      patterns: ["Anna", "Markus", "Petra", "Yannick"],
      fallbackLangs: ["de-AT", "de-CH"],
    },
    it_IT: {
      patterns: ["Alice", "Luca", "Federica", "Paola"],
      fallbackLangs: ["it-CH"],
    },
  },
  edge: {
    fr_FR: {
      patterns: ["Microsoft Hortense", "Microsoft Julie", "Microsoft Paul", "Marie"],
      fallbackLangs: ["fr-CA", "fr-BE", "fr-CH"],
    },
    en_US: {
      patterns: ["Microsoft Zira", "Microsoft David", "Microsoft Mark", "Alex"],
      fallbackLangs: ["en-GB", "en-AU", "en-CA"],
    },
    en_GB: {
      patterns: ["Microsoft Hazel", "Microsoft George", "Microsoft Susan", "Daniel"],
      fallbackLangs: ["en-US", "en-AU", "en-CA"],
    },
    es_ES: {
      patterns: ["Microsoft Helena", "Microsoft Laura", "Microsoft Pablo", "Mónica"],
      fallbackLangs: ["es-MX", "es-AR", "es-CO"],
    },
    de_DE: {
      patterns: ["Microsoft Hedda", "Microsoft Stefan", "Microsoft Katja", "Anna"],
      fallbackLangs: ["de-AT", "de-CH"],
    },
    it_IT: {
      patterns: ["Microsoft Elsa", "Microsoft Cosimo", "Microsoft Benito", "Alice"],
      fallbackLangs: ["it-CH"],
    },
  },
  unknown: {
    fr_FR: {
      patterns: ["français", "French", "Marie", "Thomas"],
      fallbackLangs: ["fr-CA", "fr-BE", "fr-CH"],
    },
    en_US: {
      patterns: ["English", "US", "American", "Alex"],
      fallbackLangs: ["en-GB", "en-AU", "en-CA"],
    },
    en_GB: {
      patterns: ["British", "UK", "English", "Daniel"],
      fallbackLangs: ["en-US", "en-AU", "en-CA"],
    },
    es_ES: {
      patterns: ["español", "Spanish", "Castilian", "Mónica"],
      fallbackLangs: ["es-MX", "es-AR", "es-CO"],
    },
    de_DE: {
      patterns: ["Deutsch", "German", "Anna", "Markus"],
      fallbackLangs: ["de-AT", "de-CH"],
    },
    it_IT: {
      patterns: ["italiano", "Italian", "Alice", "Luca"],
      fallbackLangs: ["it-CH"],
    },
  },
};

const detectBrowser = (): BrowserType => {
  if (typeof window === "undefined") return "unknown";

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (userAgent.includes("edg/")) return "edge";
  if (userAgent.includes("chrome") && !userAgent.includes("edg/")) return "chrome";
  if (userAgent.includes("firefox")) return "firefox";
  if (userAgent.includes("safari") && !userAgent.includes("chrome")) return "safari";

  return "unknown";
};

const selectBestVoice = (
  voices: SpeechSynthesisVoice[],
  targetLang: string,
  browser: BrowserType
): SpeechSynthesisVoice | null => {
  if (voices.length === 0) return null;

  const config = VOICE_CONFIG[browser]?.[targetLang];
  if (!config) {
    return voices.find((voice) => voice.lang.startsWith(targetLang.split("_")[0])) || voices[0];
  }

  const exactLangVoices = voices.filter((voice) => voice.lang === targetLang);

  for (const pattern of config.patterns) {
    const matchingVoice = exactLangVoices.find((voice) =>
      voice.name.toLowerCase().includes(pattern.toLowerCase())
    );
    if (matchingVoice) return matchingVoice;
  }

  const defaultExactVoice = exactLangVoices.find((voice) => voice.default);
  if (defaultExactVoice) return defaultExactVoice;

  if (exactLangVoices.length > 0) return exactLangVoices[0];

  for (const fallbackLang of config.fallbackLangs) {
    const fallbackVoices = voices.filter((voice) => voice.lang === fallbackLang);

    for (const pattern of config.patterns) {
      const matchingVoice = fallbackVoices.find((voice) =>
        voice.name.toLowerCase().includes(pattern.toLowerCase())
      );
      if (matchingVoice) return matchingVoice;
    }

    const defaultFallbackVoice = fallbackVoices.find((voice) => voice.default);
    if (defaultFallbackVoice) return defaultFallbackVoice;

    if (fallbackVoices.length > 0) return fallbackVoices[0];
  }

  const langFamily = targetLang.split("_")[0];
  const familyVoices = voices.filter((voice) => voice.lang.startsWith(langFamily));
  if (familyVoices.length > 0) {
    const defaultFamilyVoice = familyVoices.find((voice) => voice.default);
    return defaultFamilyVoice || familyVoices[0];
  }
  return voices[0];
};

export const useTextToSpeech = (options: UseTextToSpeechOptions = {}): UseTextToSpeechReturn => {
  const { lang = "fr-FR", rate = 1, pitch = 1, volume = 1, onStart, onEnd, onError } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [messageId, setMessageId] = useState<string | null>(null);

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      const browser = detectBrowser();
      setIsSupported(true);

      const loadVoices = () => {
        const availableVoices = speechSynthesis.getVoices();
        setVoices(availableVoices);

        if (availableVoices.length > 0 && !selectedVoice) {
          const bestVoice = selectBestVoice(availableVoices, lang, browser);
          setSelectedVoice(bestVoice);
        }
      };

      loadVoices();

      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = loadVoices;
      }

      return () => {
        if (speechSynthesis.onvoiceschanged !== undefined) {
          speechSynthesis.onvoiceschanged = null;
        }
      };
    }
    return;
  }, [lang, selectedVoice]);

  useEffect(() => {
    if (voices.length > 0) {
      const browser = detectBrowser();
      const bestVoice = selectBestVoice(voices, lang, browser);
      if (bestVoice && bestVoice !== selectedVoice) {
        setSelectedVoice(bestVoice);
      }
    }
  }, [lang, voices, selectedVoice]);

  const speak = useCallback(
    (text: string) => {
      if (!isSupported || !text.trim()) return;

      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;

      utterance.lang = lang;
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = volume;

      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      utterance.onstart = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        onStart?.();
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        utteranceRef.current = null;
        onEnd?.();
      };

      utterance.onerror = (error) => {
        setIsSpeaking(false);
        setIsPaused(false);
        utteranceRef.current = null;
        onError?.(error);
      };

      utterance.onpause = () => {
        setIsPaused(true);
      };

      utterance.onresume = () => {
        setIsPaused(false);
      };
      speechSynthesis.speak(utterance);
    },
    [isSupported, lang, rate, pitch, volume, selectedVoice, onStart, onEnd, onError]
  );

  const stop = useCallback(() => {
    if (isSupported) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      utteranceRef.current = null;
    }
  }, [isSupported]);

  const pause = useCallback(() => {
    if (isSupported && isSpeaking) {
      speechSynthesis.pause();
    }
  }, [isSupported, isSpeaking]);

  const resume = useCallback(() => {
    if (isSupported && isPaused) {
      speechSynthesis.resume();
    }
  }, [isSupported, isPaused]);

  const setVoice = useCallback((voice: SpeechSynthesisVoice) => {
    setSelectedVoice(voice);
  }, []);

  useEffect(() => {
    return () => {
      if (isSupported) {
        speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  return {
    speak,
    stop,
    pause,
    resume,
    isSpeaking,
    isPaused,
    isSupported,
    voices,
    selectedVoice,
    setVoice,
    messageId,
    setMessageId,
  };
};
